import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { Context } from "../App";
import * as Constants from "../api/constants";
import {
  readRazorpayReturnParams,
  loadPendingCheckout,
  clearPendingCheckout,
} from "../utils/razorpayReturn";
import "../partyBossTheme.css";

function getByPath(obj, path) {
  if (obj == null || !path) return undefined;
  const parts = String(path).split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

const detailsMap = [
  ["Name", "customerName"],
  ["Booking Ref Id", "bookingRefId"],
  ["Phone number", "phoneNumber"],
  ["Event Date", "eventDate"],
  ["Theatre name", "theatreName"],
  ["Selected Slot", "slotName"],
  ["Event Type", "eventType"],
  ["Status", "status"],
  ["Payment Status", "paymentStatus"],
  ["Payment Id", "paymentResponse.paymentId"],
  ["Selected seats", "totalSeats"],
  ["Extra seats", "extraSeats"],
  ["Decoration details", "selectedDecoration"],
  ["Cake details", "selectedCake"],
  ["Flowers details", "selectedFlowers"],
  ["Gifts details", "selectedGiftsDetails"],
  ["Photos details", "selectedPhotoDetails"],
  ["Customer Notes", "userDirections"],
  ["Total Invoice amount", "totalAmount"],
  ["Advance paid", "totalAmountPaid"],
  ["Final amount to be paid", "remainingAmtToPay"],
];

const EventDetails = () => {
  const [eventData, setEventData] = useState(null);
  const [syncingPayment, setSyncingPayment] = useState(false);
  const { context, setContext } = useContext(Context);

  useEffect(() => {
    document.body.classList.add("party-boss-home");
    return () => document.body.classList.remove("party-boss-home");
  }, []);

  useEffect(() => {
    const fetchEventDetails = async (bookingRefId) => {
      try {
        const response = await axios.post(Constants.GET_EVENT_BY_BOOK_REF_ID, {
          bookingRefId,
        });
        setEventData(response.data);
      } catch (error) {
        console.error("Error fetching event details:", error);
      }
    };

    const run = async () => {
      const rp = readRazorpayReturnParams();
      const pending = loadPendingCheckout();
      let bookingRefId = context?.bookingReferenceId || pending?.bookingReferenceId;

      if (rp && (pending?.eventId || pending?.bookingReferenceId)) {
        setSyncingPayment(true);
        try {
          await axios.post(Constants.UPDATE_PD, {
            id: pending?.eventId,
            paymentResponse: {
              status: "SUCCESS",
              orderId: rp.orderId,
              paymentId: rp.paymentId,
              signature: rp.signature,
            },
          });
          clearPendingCheckout();
          if (pending?.bookingReferenceId) {
            bookingRefId = pending.bookingReferenceId;
            setContext((prev) => ({
              ...(prev && typeof prev === "object" ? prev : {}),
              bookingReferenceId: pending.bookingReferenceId,
            }));
          }
        } catch (error) {
          console.error("Payment sync after Razorpay redirect failed:", error);
        } finally {
          setSyncingPayment(false);
        }
      }

      if (bookingRefId) {
        await fetchEventDetails(bookingRefId);
      }
    };

    run();
  }, [context?.bookingReferenceId, setContext]);

  const displayValue = (key) => {
    const v = getByPath(eventData, key);
    if (v === undefined || v === null || v === "") return "—";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };

  return (
    <div className="party-boss-page w-full bg-[#05020a] pb-20 pt-20 text-white md:pt-24">
      <div className="px-[5%] lg:px-[8%]">
        {eventData && (
          <div className="mx-auto max-w-3xl rounded-[28px] border border-white/15 bg-white/[0.06] p-6 shadow-2xl backdrop-blur-md md:p-10">
            <h2 className="mb-6 text-center text-2xl font-black text-[#ffd166] md:text-3xl">
              Your booking details
            </h2>
            <div className="overflow-x-auto rounded-2xl border border-white/10">
              <table className="mb-0 w-full min-w-[320px] table-auto text-left text-sm text-white/90">
                <tbody>
                  {detailsMap.map(([label, key]) => (
                    <tr
                      key={key}
                      className="border-b border-white/10 last:border-0"
                    >
                      <td className="w-2/5 py-3 pl-4 font-bold text-white/80">
                        {label}
                      </td>
                      <td className="py-3 pr-4 text-white">
                        {displayValue(key)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {!eventData && (context?.bookingReferenceId || syncingPayment) && (
          <p className="text-center text-[#c9c1d9]">
            {syncingPayment ? "Confirming your payment…" : "Loading your booking…"}
          </p>
        )}
        {!context?.bookingReferenceId && !syncingPayment && !eventData && (
          <p className="text-center text-[#c9c1d9]">
            No booking reference found. Complete a payment to see details here.
          </p>
        )}
      </div>
    </div>
  );
};

export default EventDetails;
